import { LBtn, LTableResults } from "@components";
import { ScoringBox } from "@components/LScoringBox";
import { faCheck, faVolumeHigh, faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCategoryFromCuedTitle, trialIsRecall } from "@helpers/scoringHelper";
import { convertToSentenceCase } from "@helpers/stringHelper";
import { ResponseResult, ResultSection, WordResponseApi } from "@store/generalTypes";
import { ColumnDef, createColumnHelper } from "@tanstack/table-core";
import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Track } from "./AudioPlayer/AudioPlayer";
import { PresentTargetActions } from "./presentTargetActions";
import { PresentTargetResultNotes } from "./presentTargetResultNotes";
import { indexOf, sortBy } from "lodash";

export const PresentTargetResults = ({ ...props }: PresentTargetResultsProps): JSX.Element => {
    if (!props.section) return <div className="box">Not Found</div>;

    const [fullAudioClicked, setFullAudioClicked] = useState(false);

    const handleAudioClick = (index: number): void => {
        const responses: Track[] = [];
        props.section!.result.forEach((r) => {
            r.responses.forEach((resp) =>
                responses.push({ url: resp.audioRecordingUri ?? "", targetWord: resp.targetWord })
            );
        });

        props.showAudioPlayerForStep(props.stepIndex, props.section?.title ?? "", responses, index);
    };

    const validateMethods = useForm();
    const columnHelper = createColumnHelper<WordResponseApi>();
    const defaultColumns: Array<ColumnDef<WordResponseApi, any>> = [
        {
            id: "index",
            cell: ({ row }) => <span>{row.index + 1}</span>,
            enableSorting: false,
            meta: {
                headerClassName: "is-size-7 index-cell-width",
                cellClassName: `is-vtop has-text-centered ${
                    props.isRecall ? "index-cell-width-recall" : "index-cell-width-target"
                }`
            }
        },
        columnHelper.accessor("response", {
            header: () => "Response",
            cell: ({ cell, row }) => {
                return (
                    <>
                        {convertToSentenceCase(
                            !props.isRecall && row.original.responseType === "correct"
                                ? row.original.targetWord
                                : cell.getValue() ?? "-"
                        )}
                    </>
                );
            },
            enableSorting: false,
            meta: {
                headerClassName: "is-size-7 response-cell-width has-text-weight-normal",
                cellClassName: `is-vtop ${props.isRecall ? "has-text-weight-bold" : ""}`
            }
        }),
        columnHelper.accessor("responseType", {
            header: () => "Accuracy",
            cell: ({ cell }) => {
                if (cell.getValue() === "correct") {
                    return <FontAwesomeIcon icon={faCheck} className="has-text-green" />;
                } else return <FontAwesomeIcon icon={faX} className="has-text-danger" />;
            },
            enableSorting: false,
            meta: {
                headerClassName: "is-size-7 accuracy-cell-width has-text-weight-normal",
                cellClassName: "is-vtop has-text-centered"
            }
        }),
        {
            id: "actions",
            header: () => " ",
            cell: ({ row }) => {
                return (
                    <PresentTargetActions
                        wordReponse={row.original}
                        disbaled={!fullAudioClicked && !props.audioClicked?.some((c) => c === row.index)}
                    />
                );
            },
            enableSorting: false,
            meta: {
                headerClassName: "is-size-7 has-text-weight-normal",
                cellClassName: "is-vtop show-on-hover"
            }
        },
        columnHelper.accessor("stepName", {
            header: () => "Notes",
            enableSorting: false,
            meta: {
                headerClassName: "is-size-7 notes-cell-width has-text-weight-normal",
                cellClassName: "is-vtop notes-cell-width show-on-hover"
            },
            cell: ({ row }) => {
                return <PresentTargetResultNotes notes={row.original.notes} targetResponseId={row.original.id!} />;
            }
        })
    ];

    if (!props.isRecall) {
        defaultColumns.splice(
            1,
            0,
            columnHelper.accessor("targetWord", {
                header: () => "Target Word",
                cell: ({ cell }) => (
                    <span className="has-text-weight-bold">{convertToSentenceCase(cell.getValue())}</span>
                ),
                enableSorting: false,
                meta: {
                    headerClassName: "is-size-7 response-cell-width has-text-weight-normal",
                    cellClassName: "is-vtop"
                }
            })
        );
        defaultColumns.splice(
            4,
            0,
            columnHelper.accessor("audioRecordingUri", {
                header: () => "Audio Recording",
                cell: ({ cell }) => {
                    return (
                        <LBtn
                            variation="outline"
                            onClick={() => handleAudioClick(cell.row.index)}
                            smallSpacing
                            smallTopBottomMargins
                        >
                            <FontAwesomeIcon icon={faVolumeHigh} size="sm" />
                        </LBtn>
                    );
                },
                enableSorting: false,
                meta: {
                    headerClassName: "is-size-7 no-wrap has-text-weight-normal",
                    cellClassName: "is-vtop"
                }
            })
        );
    }

    const fullSegmentAudioClicked = (audioURL: string): void => {
        setFullAudioClicked(true);

        props.showAudioPlayerForStep(props.stepIndex, props.section?.title ?? "", [{ url: audioURL }]);
    };

    const getSortedSectionResult = (): ResponseResult[] => {
        const isCuedRecall = props.section!.result.some((x) => x.trial.includes("Cued Recall"));

        if (isCuedRecall) {
            const cuedOrder = ["Fruits", "Musical Instruments", "Articles of Clothing"];

            return sortBy(props?.section?.result, (result) => {
                const cat = result.trial.includes("-") ? result.trial.split("-")[1].trim() : result.trial;
                return indexOf(cuedOrder, cat);
            });
        }

        return props.section!.result;
    };

    return (
        <FormProvider {...validateMethods}>
            <ScoringBox
                title={props.section.title}
                isRecall={props.isRecall}
                totalCorrect={props.section.totalCorrect}
                totalWords={props.section.totalWords}
                totalIncorrect={props.section.totalIncorrect}
            >
                {getSortedSectionResult().map((r, i) => (
                    <React.Fragment key={i}>
                        {trialIsRecall(r.trial) && (
                            <div
                                className={`columns${i === 0 ? (props.section?.isCued ? " mt-2" : " mb-1") : " mt-6"}
                                ${!props.section?.isCued ? " move-up-2" : " mb-0"}`}
                            >
                                <div className="column is-half">
                                    <h2 className="has-text-weight-bold">{getCategoryFromCuedTitle(r.trial)}</h2>
                                </div>
                                <div className="column is-half has-text-right move-down-2">
                                    <LBtn
                                        iconLeft={faVolumeHigh}
                                        noSpacing
                                        variation="outline"
                                        onClick={() => fullSegmentAudioClicked(r.audioRecordingUri)}
                                        verticalSpacing="0"
                                    >
                                        Full Segment Audio
                                    </LBtn>
                                </div>
                            </div>
                        )}
                        {LTableResults<WordResponseApi>(r.responses, defaultColumns, {
                            hideBorders: true,
                            hidePaging: true,
                            showStriping: true,
                            isSmall: true,
                            isRoundedBody: true,
                            pageSize: 10000
                        })}
                    </React.Fragment>
                ))}
            </ScoringBox>
        </FormProvider>
    );
};

interface PresentTargetResultsProps {
    section?: ResultSection;
    stepIndex: number;
    isRecall: boolean;
    showAudioPlayerForStep: (stepIndex: number, audioStep: string, tracks: Track[], index?: number) => void;
    audioClicked: number[];
    audioUrl?: string;
}
